import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatIDR } from "@/lib/api";
import { CheckCircle, XCircle, Warning, Spinner } from "@phosphor-icons/react";

/**
 * Reusable preview dialog for bulk Excel import (dry-run).
 * props:
 *  - preview: response payload from POST /bulk-import/*?dry_run=true
 *              { created, updated, skipped, errors, preview: [{row,action,target,partner,date,items_count,total}] }
 *  - open, onOpenChange
 *  - onConfirm: async () => void
 *  - importing: bool
 *  - title: string
 */
export default function ImportPreviewDialog({ preview, open, onOpenChange, onConfirm, importing, title = "Pratinjau Impor Excel" }) {
  const p = preview || {};
  const rows = p.preview || [];
  const hasErrors = (p.errors || []).length > 0;
  const totalDocs = (p.created || 0) + (p.updated || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{title}
            <span className="text-xs font-normal text-slate-500">— Belum ada perubahan tersimpan</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 py-2">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-[11px] uppercase text-emerald-700 tracking-wider">Baru</div>
            <div className="text-2xl font-medium text-emerald-800 tabular-nums" data-testid="preview-created">{p.created || 0}</div>
          </div>
          <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
            <div className="text-[11px] uppercase text-sky-700 tracking-wider">Diperbarui</div>
            <div className="text-2xl font-medium text-sky-800 tabular-nums" data-testid="preview-updated">{p.updated || 0}</div>
          </div>
          <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
            <div className="text-[11px] uppercase text-stone-600 tracking-wider">Dilewati</div>
            <div className="text-2xl font-medium text-stone-700 tabular-nums" data-testid="preview-skipped">{p.skipped || 0}</div>
          </div>
          <div className={`rounded-md border p-3 ${hasErrors ? "border-rose-200 bg-rose-50" : "border-stone-200 bg-stone-50"}`}>
            <div className={`text-[11px] uppercase tracking-wider ${hasErrors ? "text-rose-700" : "text-stone-600"}`}>Kesalahan</div>
            <div className={`text-2xl font-medium tabular-nums ${hasErrors ? "text-rose-800" : "text-stone-700"}`} data-testid="preview-errors">{(p.errors || []).length}</div>
          </div>
        </div>

        {hasErrors && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 space-y-0.5" data-testid="preview-errors-list">
            <div className="font-medium flex items-center gap-1 mb-1"><Warning size={14} weight="fill" />Kesalahan pada baris berikut (baris ini akan dilewati saat impor):</div>
            {(p.errors || []).map((e, i) => <div key={i}>• {e}</div>)}
          </div>
        )}

        {rows.some(r => r.warning) && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-0.5" data-testid="preview-warnings-list">
            <div className="font-medium flex items-center gap-1 mb-1"><Warning size={14} weight="fill" />Konflik nomor dokumen — baris berikut tetap diimpor, tetapi dengan nomor otomatis:</div>
            {rows.filter(r => r.warning).map((r, i) => <div key={i}>• Baris #{r.row}: {r.warning}</div>)}
          </div>
        )}

        <div className="flex-1 overflow-auto rounded-md border border-slate-200 mt-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Baris</TableHead>
                <TableHead className="w-24">Aksi</TableHead>
                <TableHead>Nomor / Ref</TableHead>
                <TableHead>Partner / Pemohon</TableHead>
                <TableHead className="w-24">Tanggal</TableHead>
                <TableHead className="w-16 text-right">Item</TableHead>
                <TableHead className="w-32 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">Tidak ada baris valid untuk diimpor.</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={i} data-testid={`preview-row-${i}`}>
                  <TableCell className="font-mono-plex text-xs text-slate-500">#{r.row}</TableCell>
                  <TableCell>
                    {r.action === "update" ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-700">Update</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Baru</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono-plex text-xs">{r.target}{r.warning ? <div className="text-amber-600 text-[10px] mt-0.5" data-testid={`preview-warn-${i}`}>{r.warning}</div> : null}</TableCell>
                  <TableCell className="text-sm">{r.partner}</TableCell>
                  <TableCell className="text-xs text-slate-600">{r.date}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{r.items_count}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{formatIDR(r.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="mt-3 flex-row justify-between items-center gap-2 sm:justify-between">
          <div className="text-xs text-slate-500">
            {totalDocs > 0 ? <><CheckCircle size={12} weight="fill" className="inline text-emerald-600 mr-1" />Siap mengimpor <b>{totalDocs} dokumen</b>.</> : <><XCircle size={12} weight="fill" className="inline text-slate-400 mr-1" />Tidak ada dokumen yang akan diimpor.</>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing} data-testid="preview-cancel-btn">Batal</Button>
            <Button onClick={onConfirm} disabled={importing || totalDocs === 0} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="preview-confirm-btn">
              {importing ? <><Spinner size={14} className="mr-1 animate-spin" />Mengimpor…</> : <>Konfirmasi & Impor</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
